import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Container, Modal as BootstrapModal, Row } from 'react-bootstrap'
import { FormProvider, useForm } from 'react-hook-form'

import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import { IntegrationServiceId } from '../../../common/integrations'

import { useLazyGetIntegrationUsersQuery, useSaveAIAgentResultMutation } from '../../../store/integrations/integrations'
import { useGetAccountQuery } from '../../../store/accountUserSettings/accountUserSettings'
import { selectDocument, setDocumentSummarySyncStatus } from '../../../store/processDocument/document'
import { selectDocumentBatchContact } from '../../../store/processDocument/documentBatch'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'

import { useExportConfirmation } from '../../../components/ConfirmationDialog/useExportConfirmation'
import { SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { Modal } from '../../../components/Modal/Modal'
import { IntegrationSavedModalBody } from '../../../components/Integrations/IntegrationSavedModalBody/IntegrationSavedModalBody'

import { SyncStatusView } from '../../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/models'

import { AccountIntegrationConfigMappingType, getAccountCRMIntegrationConfig } from '../../../models/Account'

import { ContactDropdown } from '../../MeetingSummary/CRM/ContactDropdown'
import { getContactOptions } from '../../MeetingSummary/CRM/utils'

import { getDocumentSummaryTitle } from '../DocumentSummary/utils'
import { useDocumentSummaryAiAgentResult } from '../hooks'

const _DEFAULT_CONTACT_OBJECT = 'contact'

interface FormValues {
  externalUserId?: string
}

interface DocumentSummaryIntegrationExportModalProps {
  show: boolean
  onHide: () => void
  serviceId: IntegrationServiceId
}

export const DocumentSummaryIntegrationExportModal: React.FC<DocumentSummaryIntegrationExportModalProps> = ({ show, onHide, serviceId }) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const document = useAppSelector(selectDocument)
  const contact = useAppSelector(selectDocumentBatchContact)

  const [triggerSaveAIAgentResult, { isLoading, reset: resetSaveResult }] = useSaveAIAgentResultMutation()

  const [savedSyncStatuses, setSavedSyncStatuses] = useState<SyncStatusView[] | null>(null)

  const { trackingClient } = useTracking()

  const { runMutationWithConfirmation, ConfirmationModal, accountIsLoading } = useExportConfirmation()

  const { aiAgentResult, refetchAiAgentResult } = useDocumentSummaryAiAgentResult()

  const { data: account } = useGetAccountQuery({ accountId })

  const contactObject = useMemo(() => {
    if (serviceId !== 'salesforce') {
      return null
    }

    if (!account?.data) {
      return _DEFAULT_CONTACT_OBJECT
    }

    const accountCRMIntegrationConfig = getAccountCRMIntegrationConfig(account.data, serviceId)
    if (!accountCRMIntegrationConfig) {
      return _DEFAULT_CONTACT_OBJECT
    }

    const contactMapping = accountCRMIntegrationConfig.mappings[AccountIntegrationConfigMappingType.CONTACT]
    return typeof contactMapping === 'string' ? contactMapping : (contactMapping?.objectName ?? _DEFAULT_CONTACT_OBJECT)
  }, [account, serviceId])

  const form = useForm<FormValues>()

  const updateDocumentSummarySyncStatus = useCallback(async () => {
    const aiAgentResult = await refetchAiAgentResult()
    if (aiAgentResult) {
      dispatch(setDocumentSummarySyncStatus(aiAgentResult.syncStatus))
    }
    return aiAgentResult?.syncStatus ?? []
  }, [dispatch, refetchAiAgentResult])

  const handleSavedHide = useCallback(() => {
    setSavedSyncStatuses(null)
    resetSaveResult()
    onHide()
  }, [onHide, resetSaveResult])

  const handleSave = async (data: FormValues) => {
    const { externalUserId } = data

    if (!externalUserId || !aiAgentResult) {
      return
    }

    const { id: resultId } = aiAgentResult

    await runMutationWithConfirmation(async () => {
      try {
        await triggerSaveAIAgentResult({
          accountId,
          connector: serviceId,
          body: {
            relatedTo: { id: externalUserId, type: contactObject },
            resultId,
            title: document && contact ? getDocumentSummaryTitle(document, contact) : undefined,
          },
        }).unwrap()

        trackingClient.track({ eventName: TrackEvent.DOCUMENT_SUMMARY_SAVED, eventDetails: { targetSystem: serviceId, documentId: document?.id } })

        const syncStatuses = await updateDocumentSummarySyncStatus()
        const serviceSyncStatuses = syncStatuses.filter((status) => status.serviceId === serviceId)
        setSavedSyncStatuses(serviceSyncStatuses.map((status) => ({ ...status, type: 'aiResult', name: 'Document Summary' })))

        form.reset()
      } catch (e) {
        console.error(e)
      }
    })
  }

  if (savedSyncStatuses) {
    return (
      <Modal
        show={show}
        onHide={handleSavedHide}
        centered={true}
        bodyClassName='p-0'
        body={<IntegrationSavedModalBody syncStatuses={savedSyncStatuses} serviceId={serviceId} onHide={handleSavedHide} showSidebar={false} />}
      />
    )
  }

  return (
    <>
      <Modal
        size='xl'
        show={show}
        onHide={onHide}
        header={
          <BootstrapModal.Title className='w-100 d-flex align-items-center justify-content-center'>
            Save your document summary to&nbsp;<span className='text-capitalize'>{serviceId}</span>
          </BootstrapModal.Title>
        }
        body={
          <FormProvider {...form}>
            <DocumentSummaryIntegrationExportModalBody serviceId={serviceId} />
          </FormProvider>
        }
        onFooterPrimaryAction={form.handleSubmit(handleSave)}
        footerPrimaryActionLoading={isLoading || accountIsLoading}
        footerPrimaryActionDisabled={!aiAgentResult}
        onFooterSecondaryAction={onHide}
        showFooterActions={true}
        centered={true}
      />
      <ConfirmationModal />
    </>
  )
}

interface DocumentSummaryIntegrationExportModalBodyProps {
  serviceId: IntegrationServiceId
}

const DocumentSummaryIntegrationExportModalBody: React.FC<DocumentSummaryIntegrationExportModalBodyProps> = ({ serviceId }) => {
  return (
    <Container>
      <Row className='mt-3'>
        <Col xs={12}>
          <ContactSelector serviceId={serviceId} />
        </Col>
      </Row>
    </Container>
  )
}

interface ContactSelectorProps {
  serviceId: IntegrationServiceId
}

const ContactSelector: React.FC<ContactSelectorProps> = ({ serviceId }) => {
  const accountId = useAppSelector(selectAccountId)

  const [contactsLoading, setContactsLoading] = useState<boolean>(false)
  const [contactOptions, setContactOptions] = useState<SelectOption[]>([])
  const [searchInput, setSearchInput] = useState<string | null>(null)

  const [triggerGetContacts] = useLazyGetIntegrationUsersQuery()

  useEffect(() => {
    const fetchContacts = async (connector: string) => {
      setContactsLoading(true)
      try {
        const filters = searchInput ? { name: searchInput } : {}
        const { data: contactsResponse } = await triggerGetContacts({ accountId, connector, filters })
        if (!contactsResponse) {
          return
        }
        setContactOptions(getContactOptions(contactsResponse.data, connector))
      } catch (e) {
        console.error(e)
      } finally {
        setContactsLoading(false)
      }
    }

    fetchContacts(serviceId)
  }, [accountId, searchInput, serviceId, triggerGetContacts])

  const handleSearchInputChange = async (searchInput: string) => {
    setSearchInput(searchInput)
  }

  return (
    <ContactDropdown
      fieldName='externalUserId'
      contactOptions={contactOptions}
      serviceId={serviceId}
      onSearchInputChange={handleSearchInputChange}
      isLoading={contactsLoading}
    />
  )
}
